// Public sample browser — fetches `/api/community/samples` from the
// Worker and renders rows you can audition or insert in one click.
// The first time a community sample is inserted, its bytes are
// downloaded from R2, registered with Strudel, and persisted to IDB
// so re-loads, audition, and the "playable" `s("…")` reference all
// keep working without another network trip.
//
// The browser is intentionally simple for v1: free-text search,
// optional single-tag filter, infinite scroll. No author profiles,
// no remix tree, no bulk import.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditContext } from '../editContext';
import { listCommunitySamples, type CommunitySample } from '../../cloud/api';
import { importCommunitySample } from '../../cloud/importCommunity';
import { auditionCloudSample } from '../../cloud/auditionCloudSample';
import { isCloudConfigured } from '../../cloud/env';
import { getAudioContext } from '../../strudel';
import { useSampleStore } from './SampleStoreContext';
import { makeLocalSampleStatement } from './sampleStatement';

// 350ms is short enough to feel responsive while typing and long
// enough that a moderately fast typer doesn't fire a request per
// keystroke. Same value used elsewhere in the project.
const SEARCH_DEBOUNCE_MS = 350;

export type CommunityLibraryProps = {
  onStatus: (msg: string, level?: 'ok' | 'warn' | 'err' | '') => void;
};

type LoadState = 'idle' | 'loading' | 'loadingMore' | 'error';

export function CommunityLibrary({ onStatus }: CommunityLibraryProps) {
  const ops = useEditContext();
  const { storage, refresh: refreshSampleStore } = useSampleStore();

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [items, setItems] = useState<CommunitySample[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [auditioningId, setAuditioningId] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);
  // Stop fn for the in-flight audition voice. Lets re-clicking a
  // playing row cut the voice, and switching rows hand off cleanly.
  const auditionStopRef = useRef<(() => void) | null>(null);
  // Bumped every time the query changes. loadMore checks the epoch
  // it captured at click time against the current value; if the user
  // has since changed the query, the late response is dropped instead
  // of being appended to a list that no longer represents that query.
  const queryEpoch = useRef(0);

  // Debounce the search box so typing doesn't fire a request per
  // keystroke. The dependency on raw `query` resets the timer with
  // every change.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  // Re-fetch from page 1 whenever the (debounced) query changes.
  useEffect(() => {
    queryEpoch.current += 1;
    const epoch = queryEpoch.current;
    setLoadState('loading');
    setErrorMsg(null);
    listCommunitySamples({ q: debouncedQuery || undefined })
      .then((res) => {
        if (queryEpoch.current !== epoch) return;
        setItems(res.items);
        setNextCursor(res.nextCursor);
        setLoadState('idle');
      })
      .catch((e) => {
        if (queryEpoch.current !== epoch) return;
        setErrorMsg(e instanceof Error ? e.message : String(e));
        setItems([]);
        setNextCursor(null);
        setLoadState('error');
      });
  }, [debouncedQuery]);

  useEffect(() => {
    return () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      auditionStopRef.current?.();
    };
  }, []);

  const audition = useCallback(
    async (cloud: CommunitySample) => {
      if (auditioningId === cloud.id) {
        auditionStopRef.current?.();
        auditionStopRef.current = null;
        setAuditioningId(null);
        return;
      }
      auditionStopRef.current?.();
      auditionStopRef.current = null;

      const ctx = getAudioContext();
      if (!ctx) {
        onStatus('Audio engine not ready yet — try Play first.', 'warn');
        return;
      }
      setAuditioningId(cloud.id);
      try {
        const { stop, buffer } = await auditionCloudSample(cloud.r2Key, ctx);
        auditionStopRef.current = stop;
        const ms = Math.max(50, Math.ceil(buffer.duration * 1000) + 50);
        window.setTimeout(() => {
          if (auditionStopRef.current === stop) {
            auditionStopRef.current = null;
            setAuditioningId((curr) => (curr === cloud.id ? null : curr));
          }
        }, ms);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        onStatus('Audition failed: ' + msg, 'err');
        setAuditioningId(null);
      }
    },
    [auditioningId, onStatus],
  );

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadState === 'loadingMore') return;
    const epoch = queryEpoch.current;
    setLoadState('loadingMore');
    try {
      const res = await listCommunitySamples({
        q: debouncedQuery || undefined,
        cursor: nextCursor,
      });
      // Drop the response if the query changed under us — the new
      // first-page fetch is the source of truth now.
      if (queryEpoch.current !== epoch) return;
      setItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.nextCursor);
      setLoadState('idle');
    } catch (e) {
      if (queryEpoch.current !== epoch) return;
      setErrorMsg(e instanceof Error ? e.message : String(e));
      setLoadState('error');
    }
  }, [nextCursor, loadState, debouncedQuery]);

  const insert = useCallback(
    async (cloud: CommunitySample) => {
      if (!storage) {
        onStatus('Sample storage not ready yet — try again in a moment.', 'warn');
        return;
      }
      const ctx = getAudioContext();
      if (!ctx) {
        onStatus('Audio engine not ready yet — try Play first.', 'warn');
        return;
      }
      setImportingId(cloud.id);
      try {
        const { sample } = await importCommunitySample(cloud, ctx, storage);
        // Update local rail (MINE will see the new row) + append a
        // ready-to-play statement to the song.
        refreshSampleStore();

        let insertedId: string | null = null;
        ops.update((draft) => {
          const taken = new Set<string>();
          for (const s of draft.statements) {
            if (s.kind === 'const') taken.add(s.name);
          }
          const stmt = makeLocalSampleStatement({
            sampleName: sample.name,
            takenNames: taken,
          });
          draft.statements.push(stmt);
          insertedId = stmt.id;
        });
        if (insertedId) ops.revealBlock(insertedId);

        setFlashId(cloud.id);
        if (flashTimer.current) window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setFlashId(null), 550);
        onStatus(`Imported "${sample.name}" — try s("${sample.name}").`, 'ok');
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        onStatus('Import failed: ' + msg, 'err');
      } finally {
        setImportingId(null);
      }
    },
    [storage, ops, refreshSampleStore, onStatus],
  );

  const tags = useMemo(() => collectTopTags(items), [items]);

  if (!isCloudConfigured()) {
    return <CloudUnconfigured />;
  }

  return (
    <div className="relative w-full h-full bg-zinc-950 flex flex-col min-h-0">
      <Search value={query} onChange={setQuery} visible={items.length} />

      {tags.length > 0 && <TagChips tags={tags} onPick={(t) => setQuery(t)} />}

      <div
        aria-label="Community samples"
        className="flex-1 min-h-0 overflow-y-auto px-3 pb-6 pt-1 space-y-1.5 scrollbar-thin"
      >
        {loadState === 'loading' && items.length === 0 ? (
          <Loading />
        ) : loadState === 'error' ? (
          <ErrorState message={errorMsg ?? 'Unknown error'} />
        ) : items.length === 0 ? (
          <NoMatches query={debouncedQuery} />
        ) : (
          <>
            {items.map((entry) => (
              <CommunityRow
                key={entry.id}
                entry={entry}
                flashing={flashId === entry.id}
                auditioning={auditioningId === entry.id}
                importing={importingId === entry.id}
                onAudition={() => void audition(entry)}
                onInsert={() => void insert(entry)}
              />
            ))}
            {nextCursor && (
              <LoadMoreButton
                disabled={loadState === 'loadingMore'}
                onClick={() => void loadMore()}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function collectTopTags(items: CommunitySample[]): string[] {
  const counts = new Map<string, number>();
  for (const s of items) {
    for (const t of s.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([t]) => t);
}

// ─── Pieces ─────────────────────────────────────────────────────────

function Search({
  value,
  onChange,
  visible,
}: {
  value: string;
  onChange: (v: string) => void;
  visible: number;
}) {
  return (
    <div className="px-4 pt-3 pb-2">
      <label className="relative block">
        <span className="sr-only">Search community samples</span>
        <svg
          viewBox="0 0 16 16"
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600"
          stroke="currentColor"
          strokeWidth={1.5}
          fill="none"
        >
          <circle cx={7} cy={7} r={4.5} />
          <path d="M10.5 10.5 L14 14" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          inputMode="search"
          placeholder="search community samples…"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-zinc-900/70 border border-zinc-800 focus:border-cyan-300/50 focus:bg-zinc-900 focus:outline-none rounded text-[11px] text-zinc-200 placeholder:text-zinc-600 placeholder:tracking-wide pl-8 pr-12 py-1.5 transition-colors font-body"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-zinc-600 tabular-nums tracking-wider">
          {visible}
        </span>
      </label>
    </div>
  );
}

function TagChips({
  tags,
  onPick,
}: {
  tags: string[];
  onPick: (tag: string) => void;
}) {
  return (
    <div className="px-3 pb-2 flex flex-wrap gap-1">
      {tags.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onPick(t)}
          className="text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm border border-zinc-800 bg-zinc-900 text-zinc-500 hover:text-cyan-300 hover:border-cyan-300/50 transition-colors"
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function Loading() {
  return (
    <div className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600 py-8">
      Loading…
    </div>
  );
}

function NoMatches({ query }: { query: string }) {
  return (
    <div className="text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600 py-8">
      {query ? `No matches for "${query}"` : 'No public samples yet'}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="text-center text-[10px] tracking-[0.2em] text-rose-300 py-6 px-3">
      <div className="uppercase mb-1">Couldn't load community feed</div>
      <div className="text-zinc-500 normal-case tracking-normal text-[11px]">
        {message}
      </div>
    </div>
  );
}

function CloudUnconfigured() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="font-display text-[10px] tracking-[0.3em] uppercase text-zinc-500">
        Cloud not configured
      </div>
      <p className="font-body text-[11px] leading-[1.6] text-zinc-500 max-w-[220px]">
        Set <code className="text-zinc-300">VITE_SUPABASE_URL</code>,{' '}
        <code className="text-zinc-300">VITE_WORKER_URL</code> and friends in{' '}
        <code className="text-zinc-300">.env.local</code> to browse community samples.
      </p>
    </div>
  );
}

function LoadMoreButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full px-3 py-2 mt-2 text-[10px] uppercase tracking-[0.2em] bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-cyan-300 hover:border-cyan-300/50 disabled:opacity-30 transition-colors rounded-sm"
    >
      {disabled ? 'Loading…' : 'Load more'}
    </button>
  );
}

// ─── Row ────────────────────────────────────────────────────────────

function CommunityRow({
  entry,
  flashing,
  auditioning,
  importing,
  onAudition,
  onInsert,
}: {
  entry: CommunitySample;
  flashing: boolean;
  auditioning: boolean;
  importing: boolean;
  onAudition: () => void;
  onInsert: () => void;
}) {
  // Two actions per row: row click auditions (one-shot preview, no
  // IDB write, no statement insert); the trailing import button is
  // the commit path that registers with Strudel and appends the
  // statement. Once imported, the sample appears in MINE and behaves
  // like any other.
  return (
    <div
      className={
        'group relative flex items-stretch border bg-zinc-900/50 rounded-sm overflow-hidden transition-all ' +
        (flashing
          ? 'border-cyan-300 shadow-[0_0_0_1px_rgba(103,232,249,0.6)]'
          : auditioning
            ? 'border-cyan-300/60'
            : 'border-zinc-800 hover:border-cyan-300/30')
      }
    >
      <button
        type="button"
        onClick={onAudition}
        title={auditioning ? 'Stop audition' : `Audition "${entry.name}"`}
        className="flex-1 min-w-0 flex items-center gap-2.5 px-2.5 py-2 text-left"
      >
        <div
          aria-hidden="true"
          className="shrink-0 inline-grid place-items-center w-7 h-7 rounded-sm font-display text-[8px] tracking-[0.15em] uppercase font-semibold bg-cyan-300/15 text-cyan-200"
        >
          {auditioning ? <SpinnerDot /> : 'CMM'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-zinc-100 font-body truncate">
            {entry.name}
          </div>
          <div className="text-[9px] text-zinc-500 tracking-wider tabular-nums flex items-center gap-2">
            <span>@{entry.authorHandle}</span>
            <span>·</span>
            <span>{(entry.durationMs / 1000).toFixed(2)}s</span>
            {entry.tags.length > 0 && (
              <span className="truncate">· {entry.tags.slice(0, 3).join(', ')}</span>
            )}
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={onInsert}
        disabled={importing}
        title={importing ? 'Downloading…' : `Import & insert s("${entry.name}")`}
        aria-label={`Import ${entry.name}`}
        className="shrink-0 w-8 grid place-items-center text-zinc-500 hover:text-cyan-300 disabled:text-zinc-700 disabled:cursor-not-allowed border-l border-zinc-800 transition-colors"
      >
        {importing ? <SpinnerDot /> : <PullIcon />}
      </button>
    </div>
  );
}

function PullIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={11}
      height={11}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 2 V11" />
      <path d="M4.5 7.5 L8 11 L11.5 7.5" />
      <path d="M3 13.5 H13" />
    </svg>
  );
}

function SpinnerDot() {
  return (
    <span className="inline-block w-2 h-2 rounded-full bg-cyan-300 animate-pulse" aria-hidden="true" />
  );
}
